import {
  Link,
  SidebarNavigation,
  SkeletonLoader,
  Title,
} from '@yoast/ui-library';
import classNames from 'classnames';
import { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { useGetCurrentUser } from '../../api/generated/insights';
import { useBrandAnalysisData } from '../../contexts';
import useLogoutHelper from '../../pages/Authentication/useLogoutHelper';
import SharedNavigationContent from './SharedNavigationContent';

import LogoBrandInsights from '../../assets/brand_insights_logo.svg';

const Nav = ({ className }: { className?: string }) => {
  const location = useLocation();
  const pathname = location.pathname;
  const { hostAppProps } = useBrandAnalysisData();
  const { onLogout } = hostAppProps;

  // State to manage logout process - NOTE: this forces the "Logging out..."
  // state and does not actually check whether the host app has logged out
  // successfully
  const [logoutRequested, setLogoutRequested] = useState(false);

  const { data: user, isLoading: isLoadingUser } = useGetCurrentUser({
    query: {
      select: ({ data }) => data?.data,
    },
  });

  const { mutate: logout, isPending: isLoggingOut } = useLogoutHelper(onLogout);
  const handleLogout = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setLogoutRequested(true);
    if (isLoggingOut) return;
    return logout();
  };

  return (
    <div className={classNames('ystaii-sidemenu')}>
      {/* mobile menu */}
      <div className={'ystaii-mobile-menu lg:yst-hidden'}>
        <SidebarNavigation activePath={pathname}>
          <SidebarNavigation.Mobile
            closeButtonScreenReaderText="Close sidebar"
            openButtonScreenReaderText="Open sidebar"
          >
            <SharedNavigationContent />
          </SidebarNavigation.Mobile>
        </SidebarNavigation>
      </div>

      {/* logo & user info */}
      <div
        className={classNames(
          'ystaii-menuHeader',
          'yst-mb-6 yst-mt-10 yst-flex yst-flex-row yst-justify-between yst-px-4',
          'yst-flex-col yst-items-start yst-space-y-12 yst-max-w-2xl yst-mx-auto',
        )}
      >
        <Title>
          <RouterLink className={classNames('yst-outline-none')} to="/">
            <img
              src={LogoBrandInsights}
              alt="Yoast"
              width="208"
              height="42"
              className="yst-block"
            />
          </RouterLink>
        </Title>
        <div
          className={classNames(
            'ystaii-userInfoContainer',
            'yst-flex yst-flex-col',
          )}
        >
          <div>
            <b>
              {isLoadingUser ? (
                <SkeletonLoader>Display name</SkeletonLoader>
              ) : user?.displayName}
            </b>
          </div>
          <div>
            {isLoadingUser ? (
              <SkeletonLoader>loading-email@address.com</SkeletonLoader>
            ) : user?.email}
          </div>
          <div>
            <Link
              variant="primary"
              className="yst-no-underline"
              onClick={handleLogout}
            >
              {isLoadingUser ? (
                <SkeletonLoader>
                  Sign out <span aria-hidden="true">&rarr;</span>
                </SkeletonLoader>
              ) : logoutRequested || isLoggingOut ? (
                <>Signing out...</>
              ) : (
                <>
                  Sign out <span aria-hidden="true">&rarr;</span>
                </>
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* desktop menu */}
      <div
        className={classNames(
          'ystaii-desktopMenu',
          'yst-hidden',
          'lg:yst-mb-0 lg:yst-block lg:yst-w-full lg:yst-shrink-0',
        )}
      >
        <SidebarNavigation activePath={pathname}>
          <SharedNavigationContent className={className} />
        </SidebarNavigation>
      </div>
    </div>
  );
};

export default Nav;
